import { useState } from 'react';
import {
  AlertTriangle,
  Bell,
  CalendarClock,
  ClipboardX,
  FileWarning,
  Fuel,
  IdCard,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { extractErrorMessage } from '@/lib/api-client';
import {
  useAlertRules,
  useCreateAlertRule,
  useDeleteAlertRule,
  useUpdateAlertRule,
  type AlertRule,
} from '@/features/notifications/api';

interface RuleConfig {
  type: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  description: string;
  hasDaysBefore: boolean;
}

const RULE_CONFIGS: RuleConfig[] = [
  {
    type: 'cnh_expiring',
    icon: IdCard,
    label: 'CNH vencendo',
    description: 'Avisa quando a CNH de um motorista está próxima do vencimento.',
    hasDaysBefore: true,
  },
  {
    type: 'crlv_expiring',
    icon: FileWarning,
    label: 'CRLV vencendo',
    description: 'Avisa quando o licenciamento de um veículo está próximo do vencimento.',
    hasDaysBefore: true,
  },
  {
    type: 'maintenance_upcoming',
    icon: CalendarClock,
    label: 'Manutenção próxima',
    description: 'Avisa quando uma manutenção planejada está perto da data prevista.',
    hasDaysBefore: true,
  },
  {
    type: 'maintenance_overdue',
    icon: AlertTriangle,
    label: 'Manutenção atrasada',
    description: 'Avisa imediatamente quando uma manutenção passa do prazo.',
    hasDaysBefore: false,
  },
  {
    type: 'fueling_anomaly',
    icon: Fuel,
    label: 'Anomalia de abastecimento',
    description: 'Avisa quando o sistema detecta consumo ou preço fora do padrão.',
    hasDaysBefore: false,
  },
  {
    type: 'checklist_nok',
    icon: ClipboardX,
    label: 'Checklist com NOK',
    description: 'Avisa quando uma execução de checklist registra itens reprovados.',
    hasDaysBefore: false,
  },
];

const CHANNEL_LABELS: Record<string, string> = {
  in_app: 'No painel',
  email: 'E-mail',
  // 'whatsapp' fica reservado no backend mas não exposto na UI.
  // Documentado em documentacao/12-integracoes-futuras.md
};

const RECIPIENT_LABELS: Record<string, string> = {
  admin: 'Administradores',
  manager: 'Gestores',
  driver: 'Motoristas',
};

function parseList(v: string): string[] {
  return v.split(',').map((s) => s.trim()).filter(Boolean);
}

export function AlertsSection() {
  const rulesQuery = useAlertRules();
  const create = useCreateAlertRule();
  const update = useUpdateAlertRule();
  const remove = useDeleteAlertRule();

  const [editTarget, setEditTarget] = useState<{ config: RuleConfig; rule: AlertRule | null } | null>(null);

  const rules = rulesQuery.data?.data ?? [];
  const byType = new Map(rules.map((r) => [r.type, r]));

  const handleToggle = async (config: RuleConfig, existing: AlertRule | null) => {
    try {
      if (existing) {
        await update.mutateAsync({ id: existing.id, enabled: !existing.enabled });
        toast.success(existing.enabled ? 'Alerta desativado' : 'Alerta ativado');
      } else {
        await create.mutateAsync({ type: config.type, enabled: true });
        toast.success('Alerta ativado');
      }
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Bell className="size-4" />
          Regras de alerta
        </CardTitle>
        <CardDescription>
          Configure quais alertas o sistema deve emitir e como cada um é entregue.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {rulesQuery.isLoading
          ? [1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-20 w-full" />)
          : RULE_CONFIGS.map((config) => {
              const existing = byType.get(config.type) ?? null;
              const enabled = existing?.enabled ?? false;
              const Icon = config.icon;
              return (
                <div
                  key={config.type}
                  className="flex flex-col gap-3 rounded-lg border border-border p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`flex size-9 shrink-0 items-center justify-center rounded-md ${
                        enabled
                          ? 'bg-primary/10 text-primary'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      <Icon className="size-4" />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">{config.label}</p>
                        {enabled ? (
                          <Badge variant="success" className="text-[10px]">Ativo</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px]">Inativo</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{config.description}</p>
                      {existing && enabled && (
                        <div className="flex flex-wrap gap-1 pt-1">
                          {config.hasDaysBefore && (
                            <Badge variant="outline" className="text-[10px]">
                              {existing.daysBeforeExpiry} dias antes
                            </Badge>
                          )}
                          {parseList(existing.channels).map((c) => (
                            <Badge key={c} variant="outline" className="text-[10px]">
                              {CHANNEL_LABELS[c] ?? c}
                            </Badge>
                          ))}
                          {parseList(existing.recipients).map((r) => (
                            <Badge key={r} variant="secondary" className="text-[10px]">
                              {RECIPIENT_LABELS[r] ?? r}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 self-end sm:self-center">
                    {existing && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditTarget({ config, rule: existing })}
                      >
                        Configurar
                      </Button>
                    )}
                    <Button
                      variant={enabled ? 'outline' : 'default'}
                      size="sm"
                      onClick={() => handleToggle(config, existing)}
                      disabled={create.isPending || update.isPending}
                    >
                      {enabled ? 'Desativar' : 'Ativar'}
                    </Button>
                    {existing && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8 text-destructive"
                        title="Remover regra"
                        onClick={async () => {
                          try {
                            await remove.mutateAsync(existing.id);
                            toast.success('Regra removida');
                          } catch (err) {
                            toast.error(extractErrorMessage(err));
                          }
                        }}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
      </CardContent>

      {editTarget && (
        <EditRuleDialog
          open={!!editTarget}
          onOpenChange={(o) => !o && setEditTarget(null)}
          config={editTarget.config}
          rule={editTarget.rule}
        />
      )}
    </Card>
  );
}

interface EditRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: RuleConfig;
  rule: AlertRule | null;
}

function EditRuleDialog({ open, onOpenChange, config, rule }: EditRuleDialogProps) {
  const update = useUpdateAlertRule();
  const create = useCreateAlertRule();

  const [days, setDays] = useState(String(rule?.daysBeforeExpiry ?? 30));
  const [channels, setChannels] = useState(parseList(rule?.channels ?? 'in_app,email'));
  const [recipients, setRecipients] = useState(parseList(rule?.recipients ?? 'admin,manager'));

  const toggle = (arr: string[], value: string) =>
    arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];

  const handleSubmit = async () => {
    const payload = {
      daysBeforeExpiry: Number.parseInt(days, 10) || 30,
      channels: channels.join(','),
      recipients: recipients.join(','),
    };
    try {
      if (rule) {
        await update.mutateAsync({ id: rule.id, ...payload });
      } else {
        await create.mutateAsync({ type: config.type, enabled: true, ...payload });
      }
      toast.success('Regra atualizada');
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Configurar: {config.label}</DialogTitle>
          <DialogDescription>{config.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {config.hasDaysBefore && (
            <div className="space-y-1.5">
              <Label htmlFor="days">Avisar quantos dias antes do vencimento</Label>
              <Input
                id="days"
                inputMode="numeric"
                value={days}
                onChange={(e) => setDays(e.target.value.replace(/\D/g, ''))}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>Canais de entrega</Label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(CHANNEL_LABELS).map(([k, v]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setChannels((arr) => toggle(arr, k))}
                  className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${
                    channels.includes(k)
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:bg-accent/40'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Quem recebe</Label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(RECIPIENT_LABELS).map(([k, v]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setRecipients((arr) => toggle(arr, k))}
                  className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${
                    recipients.includes(k)
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:bg-accent/40'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              create.isPending ||
              update.isPending ||
              channels.length === 0 ||
              recipients.length === 0
            }
          >
            {create.isPending || update.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
